import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  hasConnectedPaymentProviderSelector,
  hasPendingKYCPaymentProviderSelector,
  isProvidersLoadedSelector,
  paymentProvidersSelector,
} from 'domain/paySettings/selectors';

import { hasNonDisconnectedPaymentProviderSelector } from 'domain/paySettings/selectorForPermissions';
import {
  hasAnyPermissionsForCompanyPayModuleSelector,
  payModuleCompanyPermissionsSelector,
} from 'domain/env/envSelector';

import { PaymentProviderStatuses } from 'domain/paySettings/helpers';

interface UserScreenFlow {
  shouldRedirectToSettings: boolean;
  showSubscribeScreen: boolean;
  showKYCPendingScreen: boolean;
  showRequestAccessScreen: boolean;
  showWorkspace: boolean;
}

const mapStateToProps = (state: unknown) => ({
  hasNonDisconnectedPaymentProvider: hasNonDisconnectedPaymentProviderSelector(state),
  hasConnectedPaymentProvider: hasConnectedPaymentProviderSelector(state),
  hasPendingKYCPaymentProvider: hasPendingKYCPaymentProviderSelector(state),
  hasAnyPermissionsForCompanyPayModule: hasAnyPermissionsForCompanyPayModuleSelector(state),
  payModuleCompanyPermissions: payModuleCompanyPermissionsSelector(state),
  paymentProviders: paymentProvidersSelector(state),
  isProvidersLoaded: isProvidersLoadedSelector(state),
});

const useUserScreenFlow = (): UserScreenFlow => {
  const {
    hasNonDisconnectedPaymentProvider,
    hasConnectedPaymentProvider,
    hasPendingKYCPaymentProvider,
    hasAnyPermissionsForCompanyPayModule,
    payModuleCompanyPermissions,
    paymentProviders,
    isProvidersLoaded,
  } = useSelector(mapStateToProps);

  const shouldRedirectToSettings = useMemo(() => {
    // if the user has access to any connected provider - return false
    const hasAccessToConnected = paymentProviders
      .filter((p: { get: (key: string) => unknown }) => p.get('status') === PaymentProviderStatuses.connected)
      .some((provider: { get: (key: string) => unknown }) => {
        return payModuleCompanyPermissions.some(
          (permission: { get: (key: string) => unknown }) =>
            permission.get('accountId') === provider.get('id') &&
            (permission.get('isApprover') || permission.get('isController') || permission.get('isOwner')),
        );
      });

    if (hasAccessToConnected) return false;

    // if the user does not have access to any connected provider,
    // but he is the owner of a provider with a pending status - return true
    const isPendingForOwner = paymentProviders
      .filter((p: { get: (key: string) => unknown }) => p.get('status') === PaymentProviderStatuses.kycPending)
      .some((provider: { get: (key: string) => unknown }) => {
        return payModuleCompanyPermissions.some(
          (permission: { get: (key: string) => unknown }) =>
            permission.get('accountId') === provider.get('id') && permission.get('isOwner') === true,
        );
      });

    return isPendingForOwner;
  }, [payModuleCompanyPermissions, paymentProviders]);

  const showSubscribeScreen = useMemo(
    () => !hasNonDisconnectedPaymentProvider && isProvidersLoaded,
    [hasNonDisconnectedPaymentProvider, isProvidersLoaded],
  );

  const showKYCPendingScreen = useMemo(
    () => !hasConnectedPaymentProvider && hasPendingKYCPaymentProvider,
    [hasConnectedPaymentProvider, hasPendingKYCPaymentProvider],
  );

  const showRequestAccessScreen = useMemo(
    () => hasConnectedPaymentProvider && !hasAnyPermissionsForCompanyPayModule,
    [hasConnectedPaymentProvider, hasAnyPermissionsForCompanyPayModule],
  );

  return {
    shouldRedirectToSettings,
    showSubscribeScreen,
    showKYCPendingScreen,
    showRequestAccessScreen,
    showWorkspace: !showSubscribeScreen && !showKYCPendingScreen && !showRequestAccessScreen && isProvidersLoaded,
  };
};

export default useUserScreenFlow;
